import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Flame, Undo2, Check, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#FFC8DD', '#A2D2FF', '#B9FBC0', '#CDB4DB', '#FDF0D5', '#FFAFCC', '#BDE0FE', '#FFD6A5'];
const EMOJIS = ['🎯', '💧', '🚶', '📚', '🧘', '🏋️', '🌱', '💊', '🥗', '😴', '🧴', '🖊️', '🎨', '🎧', '☀️', '🍎'];

const UNIT_OPTIONS = [
  { value: 'checkin', label: 'Bara bocka av', hint: 'Klart / inte klart' },
  { value: 'glasses', label: 'Glas', hint: 'T.ex. vatten 💧' },
  { value: 'minutes', label: 'Minuter', hint: 'T.ex. läsa 20 min' },
  { value: 'steps', label: 'Steg', hint: 'T.ex. 6000 steg' },
  { value: 'reps', label: 'Reps / gånger', hint: 'T.ex. 20 armhävningar' },
  { value: 'pages', label: 'Sidor', hint: 'T.ex. 5 sidor' },
  { value: 'pushups', label: 'Armhävningar', hint: 'Fysträning' },
  { value: 'custom', label: 'Eget', hint: 'Skriv egen enhet' },
];

const FREQ_OPTIONS = [
  { value: 'daily', label: 'Varje dag' },
  { value: 'weekdays', label: 'Vardagar (mån–fre)' },
  { value: 'custom', label: 'Valda dagar' },
];

const WEEKDAYS = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

function unitDisplay(h) {
  if (h.unit === 'checkin') return '';
  if (h.unit === 'custom') return h.unit_label || 'enheter';
  const map = {
    glasses: 'glas', minutes: 'min', steps: 'steg', reps: 'reps',
    pages: 'sidor', pushups: 'armhävn.'
  };
  return map[h.unit] || '';
}

function ProgressBar({ pct, color }) {
  return (
    <div className="w-full h-3 rounded-full border-2 border-black bg-white overflow-hidden">
      <div
        className="h-full transition-all duration-500"
        style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }}
      />
    </div>
  );
}

function Heatmap({ cells, color }) {
  return (
    <div className="flex gap-[3px]">
      {cells.map((c, i) => {
        const bg = c.hit ? color : (c.active ? '#EEE' : 'transparent');
        const border = c.active ? '1px solid #999' : '1px dashed #DDD';
        return (
          <div
            key={i}
            title={`${c.day}: ${c.value}`}
            className="w-[10px] h-[10px] rounded-[2px]"
            style={{ backgroundColor: bg, border }}
          />
        );
      })}
    </div>
  );
}

function AddHabitForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    emoji: '🎯',
    color: '#FFC8DD',
    unit: 'checkin',
    unit_label: '',
    target: 1,
    frequency: 'daily',
    days_of_week: [1, 3, 5],
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Ge din vana ett namn 💗');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        target: form.unit === 'checkin' ? 1 : Number(form.target) || 1,
      };
      await axios.post(`${API}/habits/`, payload, { withCredentials: true });
      toast.success('Ny vana tillagd! 🌸');
      onCreated();
      onClose();
    } catch (err) {
      toast.error('Kunde inte spara vana');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="neo-card p-5 space-y-4 bg-white" data-testid="habit-add-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold font-['Fredoka']">Ny vana ✨</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="habit-cancel-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {/* Namn */}
        <div>
          <label className="text-sm font-bold mb-1 block">Vad vill du bygga? 💖</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="neo-input w-full"
            placeholder="t.ex. Dricka vatten"
            autoFocus
            data-testid="habit-name-input"
          />
        </div>

        {/* Emoji picker */}
        <div>
          <label className="text-sm font-bold mb-1 block">Välj emoji</label>
          <div className="flex flex-wrap gap-1">
            {EMOJIS.map(e => (
              <button
                key={e}
                type="button"
                onClick={() => setForm({ ...form, emoji: e })}
                className={`w-9 h-9 rounded-lg border-2 text-xl transition-all ${
                  form.emoji === e ? 'border-black bg-yellow-100 scale-110' : 'border-gray-200 bg-white'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        {/* Färg */}
        <div>
          <label className="text-sm font-bold mb-1 block">Färg</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setForm({ ...form, color: c })}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  form.color === c ? 'border-black scale-125' : 'border-gray-300'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Enhet */}
        <div>
          <label className="text-sm font-bold mb-1 block">Hur mäter du?</label>
          <div className="grid grid-cols-2 gap-2">
            {UNIT_OPTIONS.map(u => (
              <button
                key={u.value}
                type="button"
                onClick={() => setForm({ ...form, unit: u.value })}
                className={`p-2 rounded-lg border-2 text-left text-sm transition-all ${
                  form.unit === u.value ? 'border-black bg-yellow-50' : 'border-gray-200 bg-white'
                }`}
                data-testid={`unit-option-${u.value}`}
              >
                <div className="font-bold">{u.label}</div>
                <div className="text-xs text-gray-500">{u.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {form.unit === 'custom' && (
          <div>
            <label className="text-sm font-bold mb-1 block">Egen enhet</label>
            <input
              type="text"
              value={form.unit_label}
              onChange={(e) => setForm({ ...form, unit_label: e.target.value })}
              className="neo-input w-full"
              placeholder="t.ex. kapitel, poddar, kramar..."
              maxLength={20}
            />
          </div>
        )}

        {form.unit !== 'checkin' && (
          <div>
            <label className="text-sm font-bold mb-1 block">Dagligt mål</label>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={form.target}
              onChange={(e) => setForm({ ...form, target: e.target.value })}
              className="neo-input w-full"
              data-testid="habit-target-input"
            />
          </div>
        )}

        {/* Frekvens */}
        <div>
          <label className="text-sm font-bold mb-1 block">Hur ofta?</label>
          <div className="flex flex-col gap-1">
            {FREQ_OPTIONS.map(f => (
              <label key={f.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="frequency"
                  checked={form.frequency === f.value}
                  onChange={() => setForm({ ...form, frequency: f.value })}
                />
                <span className="text-sm">{f.label}</span>
              </label>
            ))}
          </div>
          {form.frequency === 'custom' && (
            <div className="flex flex-wrap gap-1 mt-2">
              {WEEKDAYS.map((wd, idx) => {
                const active = form.days_of_week.includes(idx);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      const next = active
                        ? form.days_of_week.filter(d => d !== idx)
                        : [...form.days_of_week, idx];
                      setForm({ ...form, days_of_week: next });
                    }}
                    className={`px-2 py-1 rounded-lg border-2 text-xs font-bold transition-all ${
                      active ? 'bg-[#B9FBC0] border-black' : 'bg-white border-gray-200'
                    }`}
                  >
                    {wd}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="neo-btn flex-1 py-2 bg-[#B9FBC0] disabled:opacity-50"
            data-testid="save-habit-btn"
          >
            {saving ? 'Sparar...' : 'Spara vana 💾'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="neo-btn px-4 py-2 bg-gray-200"
          >
            Avbryt
          </button>
        </div>
      </form>
    </div>
  );
}

function HabitCard({ habit, onLog, onUndo, onDelete }) {
  const [customValue, setCustomValue] = useState('');
  const isCheckin = habit.unit === 'checkin';
  const unitLbl = unitDisplay(habit);

  const handleQuickLog = () => {
    const val = isCheckin ? 1 : (Number(customValue) || 1);
    onLog(habit.id, val);
    setCustomValue('');
  };

  return (
    <div
      className="neo-card p-4 space-y-3 relative"
      style={{ backgroundColor: `${habit.color}30` }}
      data-testid={`habit-card-${habit.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="w-11 h-11 rounded-xl border-2 border-black flex items-center justify-center text-2xl flex-shrink-0"
            style={{ backgroundColor: habit.color }}
          >
            {habit.emoji}
          </div>
          <div className="min-w-0">
            <div className="font-bold font-['Fredoka'] text-lg truncate">{habit.name}</div>
            <div className="text-xs text-gray-600">
              {isCheckin
                ? (habit.done_today ? 'Klar idag ✓' : 'Ej bockad idag')
                : `${habit.today_value} / ${habit.target} ${unitLbl}`}
              {!habit.active_today && ' · vilodag'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {habit.streak > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-[#FDF0D5] rounded-full border-2 border-black text-sm font-bold">
              <Flame className="w-3.5 h-3.5 text-orange-500" />
              {habit.streak}d
            </div>
          )}
          <button
            onClick={() => onDelete(habit.id, habit.name)}
            className="p-1.5 hover:bg-red-100 rounded"
            data-testid={`delete-habit-${habit.id}`}
            aria-label="Ta bort vana"
          >
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>

      {/* Progress */}
      <ProgressBar pct={habit.today_pct} color={habit.color} />

      {/* Action row */}
      <div className="flex items-center gap-2">
        {isCheckin ? (
          <button
            onClick={handleQuickLog}
            disabled={!habit.active_today}
            className={`neo-btn flex-1 py-2 flex items-center justify-center gap-2 ${
              habit.done_today ? 'bg-[#B9FBC0]' : 'bg-white'
            } disabled:opacity-40`}
            data-testid={`checkin-btn-${habit.id}`}
          >
            <Check className="w-4 h-4" />
            {habit.done_today ? 'Bockad!' : 'Bocka av'}
          </button>
        ) : (
          <>
            <input
              type="number"
              min="0"
              step="any"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder="+1"
              className="neo-input w-20 text-center"
              data-testid={`value-input-${habit.id}`}
            />
            <span className="text-xs text-gray-600 flex-shrink-0">{unitLbl}</span>
            <button
              onClick={handleQuickLog}
              disabled={!habit.active_today}
              className="neo-btn flex-1 py-2 bg-white flex items-center justify-center gap-1 disabled:opacity-40"
              data-testid={`log-btn-${habit.id}`}
            >
              <Plus className="w-4 h-4" />
              Logga
            </button>
          </>
        )}
        <button
          onClick={() => onUndo(habit.id)}
          className="neo-btn px-2 py-2 bg-gray-100"
          title="Ångra senaste"
          data-testid={`undo-btn-${habit.id}`}
        >
          <Undo2 className="w-4 h-4" />
        </button>
      </div>

      {/* 30-day heatmap */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <Heatmap cells={habit.heatmap || []} color={habit.color} />
        <div className="text-xs font-bold text-gray-700 whitespace-nowrap">
          {habit.week_completion_pct}% i veckan
        </div>
      </div>
    </div>
  );
}

export default function HabitTracker() {
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const fetchHabits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/habits/`, { withCredentials: true });
      setHabits(data.items || []);
    } catch (e) {
      console.error('Failed to fetch habits:', e);
      toast.error('Kunde inte hämta vanor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHabits(); }, [fetchHabits]);

  const logHabit = async (habitId, value) => {
    try {
      await axios.post(
        `${API}/habits/${habitId}/log`,
        { value, day: new Date().toISOString().split('T')[0] },
        { withCredentials: true }
      );
      toast.success(`+${value} loggat! 🌸`);
      fetchHabits();
    } catch (e) {
      toast.error('Kunde inte logga');
    }
  };

  const undoLog = async (habitId) => {
    try {
      const { data } = await axios.post(`${API}/habits/${habitId}/undo`, {}, { withCredentials: true });
      if (data.removed) {
        toast('Ångrat 🔄', { icon: '↩️' });
      } else {
        toast('Inget att ångra idag');
      }
      fetchHabits();
    } catch (e) {
      toast.error('Kunde inte ångra');
    }
  };

  const deleteHabit = async (habitId, name) => {
    if (!window.confirm(`Ta bort "${name}"? All historik försvinner.`)) return;
    try {
      await axios.delete(`${API}/habits/${habitId}`, { withCredentials: true });
      toast.success('Vana borttagen');
      fetchHabits();
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  };

  if (loading) {
    return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar vanor... 🌷</div>;
  }

  const totalDoneToday = habits.filter(h => h.done_today).length;
  const totalActive = habits.filter(h => h.active_today).length;

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="habit-tracker">
      {/* Header banner */}
      <div className="neo-card p-5 bg-gradient-to-br from-[#FFC8DD] via-[#FFAFCC] to-[#CDB4DB] text-black">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white border-2 border-black flex items-center justify-center text-3xl">
            🌱
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-['Fredoka'] font-bold">Mina vanor</h2>
            <p className="text-sm opacity-80">
              Små steg, stora förändringar. Ingen press — bara växande. 💗
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold font-['Fredoka']">{totalDoneToday}/{totalActive}</div>
            <div className="text-xs">klara idag</div>
          </div>
        </div>
      </div>

      {/* Add form / trigger */}
      {showAdd ? (
        <AddHabitForm onClose={() => setShowAdd(false)} onCreated={fetchHabits} />
      ) : (
        <button
          onClick={() => setShowAdd(true)}
          className="neo-btn w-full py-3 bg-[#A2D2FF] flex items-center justify-center gap-2 font-bold"
          data-testid="add-habit-btn"
        >
          <Plus className="w-5 h-5" />
          Lägg till ny vana
        </button>
      )}

      {/* Habit list */}
      {habits.length === 0 ? (
        <div className="neo-card p-10 text-center bg-white">
          <div className="text-5xl mb-3">🌸</div>
          <div className="font-['Fredoka'] text-xl font-bold mb-1">Inga vanor än</div>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Börja med bara en liten sak. En sak. Du behöver inte fixa allt idag. 💗
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {habits.map(h => (
            <HabitCard
              key={h.id}
              habit={h}
              onLog={logHabit}
              onUndo={undoLog}
              onDelete={deleteHabit}
            />
          ))}
        </div>
      )}

      {/* Encouragement footer */}
      {habits.length > 0 && totalDoneToday === totalActive && totalActive > 0 && (
        <div className="neo-card p-4 bg-[#B9FBC0] flex items-center gap-3">
          <Sparkles className="w-5 h-5" />
          <div className="font-bold">
            Alla dagens vanor klara — du är fantastisk idag 🌟
          </div>
        </div>
      )}
    </div>
  );
}
